import { useState } from 'react'
import type { NtCourse } from '../../core/types'
import { activity, DAY_LABELS, SESSION_LABELS, segmentsIn, STATUS_LABEL, costTotal } from './model'
import { Empty } from '../../ui/States'
import { ArtifactList } from './ArtifactOptions'

/**
 * The course catalog — what is on offer, as a student would read it.
 *
 * Staff-readable rather than teacher-only on purpose: a teacher writing their
 * own course should be able to see what everyone else is doing, and half of
 * "is my plan any good" is answered by reading three other people's.
 *
 * Only approved courses show by default. A course still in review is a
 * conversation between two people, not an offer.
 */
export function Catalog({ courses }: { courses: NtCourse[] }) {
  const [open, setOpen] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  // Drafts never show: a draft is the teacher's own until they send it.
  const sent = courses.filter((c) => c.status !== 'draft')
  const approved = sent.filter((c) => c.status === 'approved')
  const shown = showAll ? sent : approved

  if (sent.length === 0) {
    return <Empty title="No courses yet" body="Once teachers start proposing, they show up here." />
  }

  return (
    <>
      <div className="pagehead" style={{ marginTop: 'var(--s-2)' }}>
        <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: 0 }}>
          {approved.length} approved{sent.length > approved.length && ` · ${sent.length - approved.length} still in review`}
        </p>
        {sent.length > approved.length && (
          <button className="chip" onClick={() => setShowAll((s) => !s)}>
            {showAll ? 'Approved only' : 'Show all'}
          </button>
        )}
      </div>

      {shown.map((c) => (
        <section key={c.id} className="card" style={{ marginTop: 'var(--s-2)' }}>
          <button className="cat__head" onClick={() => setOpen(open === c.id ? null : c.id)}
            aria-expanded={open === c.id}>
            <div>
              <div style={{ fontWeight: 620 }}>{c.title}</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>
                {[c.teacherName, c.room, c.block ? `${c.block} block` : null,
                  c.costItems.length ? `$${costTotal(c.costItems)}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            {c.status !== 'approved' && <span className="pill">{STATUS_LABEL[c.status]}</span>}
          </button>

          {open === c.id && (
            <div style={{ marginTop: 'var(--s-3)' }}>
              <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: '0 0 var(--s-3)' }}>{c.desc}</p>
              {(c.artifactOptions ?? []).length > 0 && (
                <div style={{ margin: '0 0 var(--s-3)' }}>
                  <div className="opt__label">What students can make for the open house</div>
                  <ArtifactList options={c.artifactOptions} />
                </div>
              )}
              {c.plan.length > 0 && (DAY_LABELS as readonly string[]).map((label, d) => {
                const both = [0, 1].flatMap((s) => segmentsIn(c.plan, d as 0 | 1 | 2 | 3, s as 0 | 1)
                  .map((p) => ({ ...p, sLabel: SESSION_LABELS[s as 0 | 1] })))
                if (!both.length) return null
                return (
                  <div key={label} className="cat__day">
                    <div className="cat__dayname">{label}</div>
                    {both.map((p, i) => (
                      <div key={i} className="cat__seg">
                        <span className="plan__dot" style={{ background: activity(p.kind).colour }} aria-hidden="true" />
                        <span style={{ fontSize: 'var(--t-sm)' }}>{p.text}</span>
                        <span className="cat__meta">{d === 3 ? 'Friday prep' : `${p.sLabel} · ${p.span > 1 ? 'both parts' : `part ${p.slot + 1}`}`}</span>
                      </div>
                    ))}
                  </div>
                )
              })}
              {c.needs && (
                <p className="opt__hint" style={{ marginTop: 'var(--s-3)' }}>
                  <strong>Arranged:</strong> {c.needs}
                </p>
              )}
            </div>
          )}
        </section>
      ))}
    </>
  )
}
